import { useState, useMemo, useEffect, useRef } from 'react';
import { projects } from '../data/projects';
import { setPageMeta } from '../utils/setPageMeta';

// Get all technologies and count their occurrences
const allKeywords = Array.from(
  new Set(projects.flatMap((p) => p.technologies))
).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

// Most important technologies to show in filter
const importantKeywords = [
  'TypeScript',
  'Rust',
  'React',
  'Vite',
  'Node.js',
  'AWS',
  'Web3',
  'Solana',
  'Ethereum',
  'React Native',
  'PostgreSQL',
  'MongoDB',
  'Smart contract',
  'NFT',
  'Terraform',
].filter(kw => allKeywords.includes(kw));

// Custom hook for scroll animations
function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return { ref, isVisible };
}

// Custom Cursor Component
function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      
      const target = e.target as HTMLElement;
      const isInteractive = target.closest('a, button');
      setIsHovering(!!isInteractive);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div 
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
      <div 
        className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
    </>
  );
}

export default function Projects() {
  const [filterKeyword, setFilterKeyword] = useState<string | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const projectsSection = useScrollAnimation(0.1);

  const filteredProjects = useMemo(
    () =>
      filterKeyword
        ? projects.filter((p) => p.technologies.includes(filterKeyword))
        : projects,
    [filterKeyword]
  );

  useEffect(() => {
    setPageMeta({
      title: 'Projects & Portfolio | LeadingNG — Web3, Mobile, GIS, AI',
      description:
        `Browse ${projects.length}+ delivered projects: Web3 and Solana, mobile apps, GIS and climate resilience mapping, fintech, identity and AI. Filter by technology.`,
      canonicalPath: '/projects',
      keywords:
        'LeadingNG portfolio, Web3 projects, Solana development, React Native apps, GIS mapping software, software case studies, blockchain consultancy',
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = (window.scrollY / documentHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <CustomCursor />
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      <section className="page-hero page-hero-projects">
        <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="container text-center">
          <span className="page-hero-label animate-fade-in">Portfolio</span>
          <h1 className="page-title animate-slide-up" style={{ animationDelay: '0.1s' }}>Projects</h1>
          <p className="page-intro animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Selected work across Web3, fintech, mobile, and platform engineering.
          </p>
        </div>
      </section>

      <section ref={projectsSection.ref} className={`section projects-section ${projectsSection.isVisible ? 'visible' : ''}`}>
        <div className="projects-wrapper">
          <div className="projects-count scroll-reveal">
            <p>Showing <strong>{filteredProjects.length}</strong> of <strong>{projects.length}</strong> projects</p>
          </div>
          <div className="projects-filter scroll-reveal">
            <span className="projects-filter-label">Filter by:</span>
            <button
              type="button"
              className={`project-filter-pill ${filterKeyword === null ? 'active' : ''}`}
              onClick={() => setFilterKeyword(null)}
            >
              All
            </button>
            {importantKeywords.map((kw, index) => (
              <button
                key={kw}
                type="button"
                className={`project-filter-pill ${filterKeyword === kw ? 'active' : ''}`}
                onClick={() => setFilterKeyword(kw)}
                style={{ animationDelay: `${0.1 + index * 0.03}s` }}
              >
                {kw}
              </button>
            ))}
          </div>
          <div className="projects-grid">
            {filteredProjects.map((p, i) => (
              <div key={i} className="project-card scroll-reveal" style={{ transitionDelay: `${0.2 + i * 0.05}s` }}>
                <div className="project-content">
                  <h3 className="project-title">{p.headline}</h3>
                  {p.link && (
                    <a href={p.link.url} target="_blank" rel="noopener noreferrer" className="project-link">
                      {p.link.label} →
                    </a>
                  )}
                  <p className="project-description">{p.description}</p>
                  <div className="project-tech" aria-label="Technologies">
                    {p.technologies.map((t, j) => (
                      <button
                        key={j}
                        type="button"
                        className={`project-tech-tag ${filterKeyword === t ? 'active' : ''}`}
                        onClick={() => setFilterKeyword(t)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {filteredProjects.length === 0 && (
            <div className="no-projects">
              <p>No projects found with the selected technology.</p>
              <button 
                type="button" 
                className="btn btn-cta btn-cta-secondary"
                onClick={() => setFilterKeyword(null)}
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </section>

      <style>{`
        /* Custom Cursor */
        .custom-cursor,
        .custom-cursor-dot {
          position: fixed;
          pointer-events: none;
          z-index: 10000;
          transform: translate(-50%, -50%);
        }
        .custom-cursor {
          width: 40px;
          height: 40px;
          border: 2px solid var(--color-primary);
          border-radius: 50%;
          transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease;
          opacity: 0.5;
        }
        .custom-cursor.hovering {
          width: 60px;
          height: 60px;
          border-color: var(--color-secondary);
          opacity: 0.8;
        }
        .custom-cursor-dot {
          width: 6px;
          height: 6px;
          background: var(--color-primary);
          border-radius: 50%;
          transition: transform 0.15s ease, background 0.3s ease;
        }
        .custom-cursor-dot.hovering {
          transform: translate(-50%, -50%) scale(1.5);
          background: var(--color-secondary);
        }

        /* Scroll Progress Bar */
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }

        /* Hero animations */
        .animate-fade-in {
          opacity: 0;
          animation: fadeIn 1s ease-out forwards;
        }
        .animate-slide-up {
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 0.8s ease-out forwards;
        }
        @keyframes fadeIn {
          to { opacity: 1; }
        }
        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .visible .scroll-reveal {
          opacity: 1;
          transform: translateY(0);
        }

        .page-hero-projects {
          position: relative;
          padding: 88px 20px 64px;
          overflow: hidden;
          color: #fff;
        }
        .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero-projects > .container {
          position: relative;
          z-index: 1;
        }
        .page-hero-label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .page-title { margin: 0 0 12px; font-size: 2rem; font-weight: 700; letter-spacing: -0.02em; }
        .page-intro { margin: 0; opacity: 0.92; font-size: 1.05rem; }
        .projects-section { 
          background: linear-gradient(180deg, #f5f6f8 0%, #e8eaed 100%); 
          padding-top: 48px; 
          padding-bottom: 64px; 
          position: relative;
        }
        .projects-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .projects-count {
          text-align: center;
          margin-bottom: 24px;
        }
        .projects-count p {
          font-size: 0.95rem;
          color: var(--color-text-muted);
          margin: 0;
        }
        .projects-count strong {
          color: var(--color-primary);
          font-weight: 600;
        }
        .projects-filter {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px 12px;
          margin-bottom: 36px;
        }
        .projects-filter-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text-muted);
          margin-right: 4px;
        }
        .visible .project-filter-pill {
          opacity: 0;
          animation: fadeInUp 0.5s ease-out forwards;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .project-filter-pill {
          font-size: 0.8rem;
          padding: 6px 12px;
          background: #fff;
          color: var(--color-secondary);
          border: 1px solid var(--color-border);
          border-radius: 999px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .project-filter-pill::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(var(--color-primary-rgb), 0.1);
          transform: translate(-50%, -50%);
          transition: width 0.4s ease, height 0.4s ease;
        }
        .project-filter-pill:hover::before {
          width: 150%;
          height: 150%;
        }
        .project-filter-pill:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .project-filter-pill.active {
          background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
          color: #fff;
          border-color: var(--color-primary);
          box-shadow: 0 4px 12px rgba(var(--color-primary-rgb), 0.3);
        }

        /* Projects Grid Layout */
        .projects-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }
        .project-card {
          background: #fff;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          padding: 28px;
          transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;
          box-shadow: var(--shadow-sm);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .project-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, var(--color-primary), var(--color-secondary));
          transform: scaleY(0);
          transition: transform 0.3s ease;
          transform-origin: top;
        }
        .project-card:hover::before {
          transform: scaleY(1);
        }
        .project-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-4px);
          border-color: var(--color-primary);
        }
        .project-content {
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .project-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--color-bg-dark);
          margin: 0 0 12px;
          transition: color 0.3s ease;
          line-height: 1.3;
        }
        .project-card:hover .project-title {
          color: var(--color-primary);
        }
        .project-link {
          display: inline-block;
          font-size: 0.9rem;
          color: var(--color-link);
          margin-bottom: 12px;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s ease;
        }
        .project-link:hover {
          color: var(--color-primary);
          text-decoration: underline;
        }
        .project-description {
          font-size: 0.95rem;
          color: var(--color-text-muted);
          line-height: 1.6;
          margin: 0 0 16px;
          flex: 1;
        }
        .project-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: auto;
        }
        .project-tech-tag {
          font-size: 0.75rem;
          padding: 4px 10px;
          background: #f5f6f8;
          color: var(--color-secondary);
          border-radius: 999px;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid transparent;
          transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.2s ease;
        }
        .project-tech-tag:hover {
          background: rgba(var(--color-primary-rgb), 0.1);
          color: var(--color-primary);
          border-color: var(--color-primary);
          transform: translateY(-2px);
        }
        .project-tech-tag.active {
          background: var(--color-primary);
          color: #fff;
          border-color: var(--color-primary);
        }
        .project-card:hover .project-tech-tag:not(.active) {
          background: rgba(var(--color-primary-rgb), 0.05);
        }

        /* No projects message */
        .no-projects {
          text-align: center;
          padding: 60px 20px;
        }
        .no-projects p {
          font-size: 1.1rem;
          color: var(--color-text-muted);
          margin: 0 0 24px;
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in,
          .animate-slide-up,
          .scroll-reveal {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
          .scroll-progress-bar {
            height: 2px;
          }
          .projects-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .project-card {
            padding: 24px;
          }
        }

        @media (max-width: 480px) {
          .project-card {
            padding: 20px;
          }
          .project-title {
            font-size: 1.05rem;
          }
          .project-description {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </>
  );
}
